/**
 * TechnicalProductsPanel
 *
 * Right column — shows technical products for the selected commercial product.
 * Admins toggle checkboxes and click Save to commit changes.
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import './TechnicalProductsPanel.css';

const TechnicalProductsPanel = ({
  selectedCommercial,
  technicalProducts,
  onSave,
  onCreateNew,
  onDirtyChange,
  loading,
  saving,
  isAdmin,
}) => {
  const [checked, setChecked] = useState({});
  const [initialChecked, setInitialChecked] = useState({});
  const [dirty, setDirty] = useState(false);

  // Sync checkbox state when technicalProducts or selected commercial changes
  useEffect(() => {
    if (!technicalProducts) return;
    const initial = {};
    technicalProducts.forEach((p) => {
      initial[p.id] = p.linked;
    });
    setChecked(initial);
    setInitialChecked(initial);
    setDirty(false);
    onDirtyChange?.(false);
  }, [technicalProducts, selectedCommercial?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggle = (id) => {
    setChecked((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      const isDirty = Object.keys(next).some((k) => next[k] !== initialChecked[k]);
      setDirty(isDirty);
      onDirtyChange?.(isDirty);
      return next;
    });
  };

  const handleSave = () => {
    const selectedIds = Object.entries(checked)
      .filter(([, v]) => v)
      .map(([k]) => k); // UUIDs — keep as strings
    onSave(selectedIds);
    setDirty(false);
    onDirtyChange?.(false);
  };

  const handleDiscard = () => {
    setChecked({ ...initialChecked });
    setDirty(false);
    onDirtyChange?.(false);
  };

  if (!selectedCommercial) {
    return (
      <div className="ph-panel">
        <div className="ph-panel__header">
          <h3 className="ph-panel__title">Technical Products</h3>
        </div>
        <div className="ph-panel__body">
          <div className="ph-panel__placeholder-text">
            Select a commercial product to view technical products
          </div>
          {isAdmin && (
            <div className="ph-panel__actions">
              <button className="ph-panel__create-btn ph-tp__create-btn" onClick={() => onCreateNew()} type="button">
                + New Technical Product
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  const linkedCount = Object.values(checked).filter(Boolean).length;

  return (
    <div className="ph-panel">
      <div className="ph-panel__header">
        <h3 className="ph-panel__title">Technical Products</h3>
        <span className="ph-panel__count">{linkedCount} linked</span>
      </div>

      <div className="ph-panel__body">
        {loading ? (
          <div className="ph-panel__loading">
            <div className="ph-panel__spinner" />
          </div>
        ) : !technicalProducts || technicalProducts.length === 0 ? (
          <div className="ph-panel__empty">No technical products available</div>
        ) : (
          <>
            <ul className="ph-panel__list ph-panel__list--checkboxes">
              {technicalProducts.map((prod) => {
                const isChecked = !!checked[prod.id];
                const wasLinked = !!initialChecked[prod.id];
                const isAdded = isChecked && !wasLinked;
                const isRemoved = !isChecked && wasLinked;
                return (
                <li key={prod.id}>
                  <label
                    className={`ph-panel__checkbox-row ${isChecked ? 'ph-panel__checkbox-row--checked' : ''} ${isAdded ? 'ph-panel__checkbox-row--added' : ''} ${isRemoved ? 'ph-panel__checkbox-row--removed' : ''} ${!isAdmin ? 'ph-panel__checkbox-row--readonly' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => isAdmin && handleToggle(prod.id)}
                      disabled={!isAdmin}
                      className="ph-panel__checkbox"
                    />
                    <span className="ph-panel__item-name">{prod.product_name}</span>
                  </label>
                </li>
                );
              })}
            </ul>

            {isAdmin && (
              <div className="ph-tp__footer">
                {dirty && (
                  <span className="ph-tp__unsaved-warning">Unsaved changes</span>
                )}
                <div className="ph-tp__footer-buttons">
                  {isAdmin && (
                    <button
                      className="ph-panel__create-btn ph-tp__create-btn"
                      onClick={() => onCreateNew()}
                      type="button"
                    >
                      + New Technical Product
                    </button>
                  )}
                  {dirty && (
                    <>
                      <button
                        className="ph-tp__discard-btn"
                        onClick={handleDiscard}
                        type="button"
                      >
                        Discard
                      </button>
                      <button
                        className="ph-tp__save-btn"
                        onClick={handleSave}
                        disabled={saving}
                        type="button"
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

TechnicalProductsPanel.propTypes = {
  selectedCommercial: PropTypes.object,
  technicalProducts: PropTypes.array,
  onSave: PropTypes.func.isRequired,
  onCreateNew: PropTypes.func.isRequired,
  onDirtyChange: PropTypes.func,
  loading: PropTypes.bool,
  saving: PropTypes.bool,
  isAdmin: PropTypes.bool,
};

export default TechnicalProductsPanel;
